import type { ChainFamily } from 'src/blockchain';
import type { EscrowCreation } from 'src/types/chain';
import type { ApiDeliveryDeal, ApiDeliveryDealDetail } from 'src/types/delivery';

import { DeliveryGasStatus, DeliveryDealStatus } from 'src/types/delivery';

/**
 * Pure "which buyer action is available in this status" logic for the delivery deal
 * screen (deal v2, spec §4.9/§4.14/§7). Mirrors the api's status rules
 * (`delivery-deal.rules.ts` + the contract guards) so the UI never shows a button the
 * server would reject. Kept pure for node-env unit tests (no RTL in the app).
 */

/** Terminal statuses — nothing left to do (spec §4.9). */
export const DELIVERY_TERMINAL_STATUSES: DeliveryDealStatus[] = [
  DeliveryDealStatus.HandedOver,
  DeliveryDealStatus.OfferExpired,
  DeliveryDealStatus.Declined,
  DeliveryDealStatus.Withdrawn,
  DeliveryDealStatus.ApprovalExpired,
  DeliveryDealStatus.Cancelled,
  DeliveryDealStatus.Refunded,
];

/** Pre-payment statuses a buyer may Withdraw from (spec §7, before any money). */
const WITHDRAWABLE: DeliveryDealStatus[] = [
  DeliveryDealStatus.Offered,
  DeliveryDealStatus.Approved,
  DeliveryDealStatus.AwaitingPayment,
];

/** Statuses a buyer may Confirm from (triggers the on-chain deploy, spec §4.2/§7). */
const CONFIRMABLE: DeliveryDealStatus[] = [
  DeliveryDealStatus.Approved,
  DeliveryDealStatus.AwaitingPayment,
];

export const isDeliveryTerminal = (status: DeliveryDealStatus): boolean =>
  DELIVERY_TERMINAL_STATUSES.includes(status);

export interface DeliveryActionVisibility {
  /** Buyer can withdraw the offer (pre-money). */
  canWithdraw: boolean;
  /** Buyer can Confirm — start / retry the clone deploy (S2). */
  canConfirm: boolean;
  /**
   * The platform's deploy is in flight: confirmed, no contract address yet, no terms sheet, and the
   * chain is one where the platform creates the contract (TRON) — the deal is waiting on the api,
   * not on the buyer. Never on a chain whose registry says the buyer creates it (api#380): there a
   * confirmed deal with no sheet is one whose sheet the api failed to sign, and Confirm is the retry.
   */
  isDeploying: boolean;
  /**
   * The buyer's own wallet is to create the escrow contract (api#380): `awaiting_payment`, no
   * contract yet, and the api has signed a terms sheet for it. The pay dialog then runs three
   * prompts — Create escrow, approve, fund. Keyed on the status, never on "has a contract": a clone
   * created at the edge of the payment window may be adopted by the expiry sweep while the deal
   * is still closed as `approval_expired`, and that deal must offer no payment.
   */
  needsEscrow: boolean;
  /** The buyer can pay: a deployed contract (approve + fund), or a sheet to create one from first. */
  canPay: boolean;
  /** Buyer can cancel on-chain (funds escrowed, not disputed/picked up), spec §4 S3. */
  canCancel: boolean;
  /** Buyer can raise a dispute on-chain (funds escrowed, not already disputed). */
  canRaiseDispute: boolean;
  /** Gas compensation is still owed — show the standalone "Pay gas fee" tail (spec §4.3). */
  canPayGas: boolean;
  /** The one-time handover code is available to the buyer (from `escrowed`, spec §4.11). */
  showHandoverCode: boolean;
  /** No further buyer action — a terminal status. */
  isTerminal: boolean;
}

/**
 * Compute the visible buyer actions for a delivery deal. `canPay` needs a deployed
 * `contractAddress` or a terms sheet to create one from (`needsEscrow`); `canPayGas` is an
 * independent, non-blocking tail that shows once money has moved on-chain (a contract exists) and
 * the deal is still live. `deployTxHash` is deliberately not read anywhere here: a contract the
 * poller adopted from the factory registry may carry none (api#380), and the buttons must not care.
 */
/** What the deal's chain registry entry says about who creates the contract (api#380). */
export interface DeliveryActionOptions {
  escrowCreation?: EscrowCreation;
}

export const deliveryActionVisibility = (
  deal: Pick<ApiDeliveryDeal, 'status' | 'contractAddress' | 'gasStatus' | 'handoverCode'> &
    Partial<Pick<ApiDeliveryDealDetail, 'escrowTerms'>>,
  options?: DeliveryActionOptions
): DeliveryActionVisibility => {
  const { status, contractAddress, gasStatus, handoverCode, escrowTerms } = deal;
  const terminal = isDeliveryTerminal(status);
  const hasContract = !!contractAddress;
  const awaitingPayment = status === DeliveryDealStatus.AwaitingPayment;

  const needsEscrow = awaitingPayment && !hasContract && !!escrowTerms;
  // Confirm is the retry of the platform's deploy, or of the api's signing; with a sheet there is
  // nothing for the api to retry.
  const canConfirm = CONFIRMABLE.includes(status) && !hasContract && !needsEscrow;
  const isDeploying = awaitingPayment && !hasContract && !needsEscrow && options?.escrowCreation !== 'buyer';
  const canPay = awaitingPayment && (hasContract || needsEscrow);

  return {
    // Once the contract is live (deploy done), the buyer pays rather than withdraws.
    canWithdraw: WITHDRAWABLE.includes(status) && !hasContract,
    canConfirm,
    isDeploying,
    needsEscrow,
    canPay,
    // cancel()/raiseDispute() contract guards: isFunded && !isDisputed && !finalized → `escrowed`.
    canCancel: status === DeliveryDealStatus.Escrowed,
    canRaiseDispute: status === DeliveryDealStatus.Escrowed,
    // Gas is a non-blocking operational tail: only meaningful once escrow money moved
    // (a contract exists) and the deal is not finished; the code presence is api-driven.
    canPayGas: gasStatus === DeliveryGasStatus.Pending && hasContract && !terminal,
    showHandoverCode: !!handoverCode,
    isTerminal: terminal,
  };
};

/**
 * Everything a buyer's own signature can change about their deal, as one comparable string.
 *
 * Used to decide when an on-chain action has actually landed: the screen keeps the progress panel
 * up, and the buttons locked, until the deal differs from what it was when the signature was sent.
 *
 * The status alone is not enough, and the gap is a real one rather than a hypothetical: the first
 * vote in a dispute leaves the deal `disputed` and changes only the buyer's own vote, so a
 * status-only comparison would spin on "updating your deal" over a deal that had already updated.
 */
export const deliveryDealFingerprint = (deal: Pick<ApiDeliveryDealDetail, 'status' | 'buyerVote'>): string =>
  `${deal.status}:${deal.buyerVote}`;

/**
 * The first contract generation whose dispute is settled by the parties' own signatures
 * ("2 of 3"). Below it, the clone has no `signResolution`, no `claimStaleDispute` and no
 * `getDispute` — its only resolution path is the platform's `resolve(bool)`. api pins the
 * numbers: 1 = the TRON clone, 3 = v3 (`DELIVERY_CONTRACT_VERSION_*`, api#178).
 */
export const DELIVERY_SIGNED_DISPUTE_FROM_VERSION = 3;

/**
 * Whether this deal's contract has the two-of-three signatures at all.
 *
 * `contractVersion` is what api says the deployed clone is, and it wins whenever present. The
 * family is only the fallback for an api older than the field — exact today (TRON runs the old
 * clone, EVM runs v3) and wrong the day v3 reaches TRON, which is why it is the fallback and not
 * the rule. With neither known, nothing is signable: a transaction sent to the wrong generation
 * reverts after the buyer paid the network fee for it.
 */
export const deliveryDisputeSupportsSignatures = (
  deal: Pick<ApiDeliveryDeal, 'contractVersion'>,
  family: ChainFamily | undefined
): boolean => {
  if (typeof deal.contractVersion === 'number') {
    return deal.contractVersion >= DELIVERY_SIGNED_DISPUTE_FROM_VERSION;
  }

  return family === 'evm';
};

/** What the buyer can do inside an open dispute (contracts v3, onchain part §3.1). */
export interface DisputeActionVisibility {
  /** The deal is in a dispute — show the votes and the signing panel at all. */
  isDisputed: boolean;
  /**
   * This deal's contract has the two-of-three signatures at all. False on the pre-v3 clone, where
   * GAC settles the dispute and every signature button would revert.
   */
  supportsSignatures: boolean;
  /** `signResolution(bool)`: the buyer may cast or overwrite their vote while nothing is executed. */
  canSign: boolean;
  /**
   * `claimStaleDispute()`: after 14 days of a dispute nobody resolved, the buyer takes the refund
   * alone. The contract owns the deadline; this only decides when to stop disabling the button.
   */
  canClaimStale: boolean;
  /** ISO moment the stale claim opens, for the "available on …" line while it is still shut. */
  staleClaimAvailableAt: string | null;
}

/**
 * The dispute half of the screen (contracts v3 §3.2/§5).
 *
 * Both actions are the contract's, not api's: the buyer signs with their own wallet and the poller
 * brings back the outcome. The guards here mirror the contract's own (`isDisputed && !finalized`,
 * and the timeout for the stale claim) so the app never offers a button whose transaction would
 * revert — the wallet fee for a reverted call is the buyer's, and "nothing happened, try again"
 * is the worst answer an escrow can give.
 *
 * The generation gate comes first: on a clone without signatures the dispute is still shown, but
 * as GAC's to settle — no vote to cast, no deadline to wait for (app#192).
 */
export const disputeActionVisibility = (
  deal: Pick<ApiDeliveryDealDetail, 'status' | 'staleClaimAvailableAt' | 'contractVersion'>,
  /** The deal's wallet family — the fallback for the generation when api did not say it. */
  family: ChainFamily | undefined,
  now: number = Date.now()
): DisputeActionVisibility => {
  const isDisputed = deal.status === DeliveryDealStatus.Disputed;
  const supportsSignatures = deliveryDisputeSupportsSignatures(deal, family);
  const availableAt = supportsSignatures ? (deal.staleClaimAvailableAt ?? null) : null;

  return {
    isDisputed,
    supportsSignatures,
    canSign: isDisputed && supportsSignatures,
    canClaimStale: isDisputed && availableAt !== null && now >= new Date(availableAt).getTime(),
    staleClaimAvailableAt: availableAt,
  };
};
